import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Leaf, Flame, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";

const IMAGES = {
  hero: [
    "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/avi6rx8r_WhatsApp%20Image%202026-02-28%20at%2014.45.44%20%283%29.jpeg",
    "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/7hts6rhl_WhatsApp%20Image%202026-02-28%20at%2014.45.44.jpeg",
    "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/fy8rgjs0_WhatsApp%20Image%202026-02-28%20at%2014.15.29%20%281%29.jpeg",
    "https://customer-assets.emergentagent.com/job_9aafd9a9-bdf8-4d35-9ad3-a45b89c1eed6/artifacts/ptenx1og_WhatsApp%20Image%202026-04-05%20at%2015.02.47.jpeg",
    "https://customer-assets.emergentagent.com/job_9aafd9a9-bdf8-4d35-9ad3-a45b89c1eed6/artifacts/9wfd2p0a_WhatsApp%20Image%202026-04-05%20at%2015.28.58.jpeg",
  ],
  features: [
    "https://customer-assets.emergentagent.com/job_9aafd9a9-bdf8-4d35-9ad3-a45b89c1eed6/artifacts/bgi62tnf_image.png",
    "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/dlpsv85t_WhatsApp%20Image%202026-02-28%20at%2014.15.29.jpeg",
    "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/lg6ur0og_WhatsApp%20Image%202026-02-28%20at%2014.45.46%20%281%29.jpeg",
  ]
};

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % IMAGES.hero.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="hero-section" data-testid="hero-section">
      {/* Background Images */}
      {IMAGES.hero.map((img, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={img}
            alt={`Weaver's Nest view ${index + 1}`}
            className="hero-image animate-ken-burns"
          />
        </div>
      ))}
      
      {/* Overlay */}
      <div className="absolute inset-0 hero-overlay" />
      
      {/* Content */}
      <div className="hero-content">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-sm md:text-base uppercase tracking-[0.3em] text-white/80 mb-4"
        >
          Sardinia Bay, Gqeberha
        </motion.p>
        
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="font-heading text-5xl md:text-7xl lg:text-8xl font-medium mb-6 leading-tight"
        >
          Weaver's Nest
          <span className="block text-3xl md:text-4xl lg:text-5xl font-normal italic mt-2">
            Retreat
          </span>
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-lg md:text-xl text-white/90 max-w-2xl mb-10 leading-relaxed"
        >
          An oasis of tranquility and luxury nestled between ancient Milkwoods 
          and indigenous bush. Your perfect escape awaits.
        </motion.p>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <a 
            href="https://api.whatsapp.com/send?phone=%2B27832329940&text=Hi%2C%20I%20would%20like%20to%20check%20availability%20at%20Weaver%27s%20Nest%20Cottage" 
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-book-btn"
          >
            <Button className="btn-primary text-lg px-10 py-6">
              Check Availability
            </Button>
          </a>
          <Link to="/the-space" data-testid="hero-explore-btn">
            <Button variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-brand-charcoal rounded-full px-10 py-6 text-lg">
              Explore the Space
            </Button>
          </Link>
        </motion.div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-3 z-20">
        {IMAGES.hero.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentSlide ? "bg-white w-8" : "bg-white/50"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

const HighlightsSection = () => {
  const highlights = [
    {
      icon: Flame,
      title: "Outdoor Fire Pit",
      description: "Gather around the cozy fire pit for memorable evenings under the stars.",
      image: IMAGES.features[0]
    },
    {
      icon: Leaf,
      title: "Nature Immersion",
      description: "Wake up to birdsong and fall asleep to the sounds of the bush.",
      image: IMAGES.features[1]
    },
    {
      icon: Heart,
      title: "Wedding Ready",
      description: "The perfect setting for bridal parties and intimate celebrations.",
      image: IMAGES.features[2]
    }
  ];

  return (
    <section className="py-20 md:py-32 bg-white" data-testid="highlights-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="section-subheading">Experience</p>
          <h2 className="section-heading">Why Choose Weaver's Nest</h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {highlights.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group"
            >
              <div className="img-zoom rounded-2xl overflow-hidden mb-6 aspect-[4/3]">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-brand-sage/10 flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-brand-sage" />
                </div>
                <h3 className="font-heading text-xl">{item.title}</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WeddingCTA = () => (
  <section className="py-20 md:py-32 relative overflow-hidden" data-testid="wedding-cta">
    <div className="absolute inset-0">
      <img
        src="https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/lg6ur0og_WhatsApp%20Image%202026-02-28%20at%2014.45.46%20%281%29.jpeg"
        alt="Wedding setting"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-brand-charcoal/60" />
    </div>
    
    <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-2xl text-white"
      >
        <p className="text-sm uppercase tracking-[0.3em] text-white/70 mb-4">
          For Brides & Bridal Parties
        </p>
        <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-6">
          A Beautiful Space for Wedding Mornings
        </h2>
        <p className="text-lg text-white/80 leading-relaxed mb-10">
          Calm. Private. Light-filled. Just minutes from Port Elizabeth's top wedding venues. 
          The perfect setting to prepare for your special day.
        </p>
        <Link to="/weddings" data-testid="wedding-cta-btn">
          <Button className="bg-white text-brand-charcoal hover:bg-white/90 rounded-full px-10 py-6 text-lg font-medium">
            Explore Bridal Stays
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
      </motion.div>
    </div>
  </section>
);

const BookingCTA = () => (
  <section className="py-20 md:py-32 bg-white" data-testid="booking-cta">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <p className="section-subheading">Ready to Escape?</p>
        <h2 className="section-heading mb-6">Book Your Stay</h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-10">
          Book directly with us via WhatsApp and save on platform fees. Experience the magic of 
          Weaver's Nest Cottage for yourself.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a 
            href="https://api.whatsapp.com/send?phone=%2B27832329940&text=Hi%2C%20I%20would%20like%20to%20book%20a%20stay%20at%20Weaver%27s%20Nest%20Cottage" 
            target="_blank"
            rel="noopener noreferrer"
            data-testid="booking-cta-btn"
          >
            <Button className="btn-primary text-lg px-10 py-6">
              Book via WhatsApp
            </Button>
          </a>
          <Link to="/contact" data-testid="contact-cta-btn">
            <Button className="btn-secondary text-lg px-10 py-6">
              Contact Us
            </Button>
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <HeroSection />
      <HighlightsSection />
      <WeddingCTA />
      <BookingCTA />
    </div>
  );
}
